import { memo, useMemo } from 'react';

type DateFormat = 'short' | 'long' | 'relative';

interface DateCellProps {
  value: Date | string | number | null | undefined;
  format?: DateFormat;
  locale?: string;
}

// Relative time formatting
function getRelativeTime(date: Date, locale: string): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  // Future dates
  if (diffMs < 0) {
    const absDiffDay = Math.abs(diffDay);
    if (absDiffDay === 0) return locale === 'tr-TR' ? 'Bugün' : 'Today';
    if (absDiffDay === 1) return locale === 'tr-TR' ? 'Yarın' : 'Tomorrow';
    if (absDiffDay < 7) return locale === 'tr-TR' ? `${absDiffDay} gün sonra` : `In ${absDiffDay} days`;
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  }

  // Past dates
  if (diffSec < 60) return locale === 'tr-TR' ? 'Az önce' : 'Just now';
  if (diffMin < 60) return locale === 'tr-TR' ? `${diffMin} dk önce` : `${diffMin}m ago`;
  if (diffHour < 24) return locale === 'tr-TR' ? `${diffHour} saat önce` : `${diffHour}h ago`;
  if (diffDay === 1) return locale === 'tr-TR' ? 'Dün' : 'Yesterday';
  if (diffDay < 7) return locale === 'tr-TR' ? `${diffDay} gün önce` : `${diffDay}d ago`;
  if (diffWeek < 4) return locale === 'tr-TR' ? `${diffWeek} hafta önce` : `${diffWeek}w ago`;
  if (diffMonth < 12) return locale === 'tr-TR' ? `${diffMonth} ay önce` : `${diffMonth}mo ago`;
  return locale === 'tr-TR' ? `${diffYear} yıl önce` : `${diffYear}y ago`;
}

export const DateCell = memo(({
  value,
  format = 'short',
  locale = 'tr-TR'
}: DateCellProps) => {
  const date = useMemo(() => {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }, [value]);

  if (!date) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const formattedDate = useMemo(() => {
    switch (format) {
      case 'long':
        return date.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'short'
        });
      case 'relative':
        return getRelativeTime(date, locale);
      case 'short':
      default:
        // DD.MM.YYYY formatı (Türkçe standart)
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }
  }, [date, format, locale]);

  const title = date.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <span
      title={title}
      style={{
        fontFamily: format === 'relative' ? 'var(--font-body)' : 'var(--font-mono)',
        fontSize: '12px',
        color: format === 'relative' ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
        whiteSpace: 'nowrap'
      }}
    >
      {formattedDate}
    </span>
  );
});

DateCell.displayName = 'DateCell';
